/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
* 链接离线批量下载类
* @constructor
* @author : lushaoqing
* @date : 2023/04/13
*
*/

import { safeJSONParse } from '@shared/utils'
import EventEmitter from '@remote/utils/event-emitter'
import { submitLixianTasks, createLixianTasks } from '@remote/api/adapter/shell-mqtt'
import { guid, matchBatchBtLink, isOtherTypeBTLink, isMagnetTypeBTLink } from '@remote/api/adapter/helper'
import { IJobInfo, IPoll2queryJobparams, getJobInfo, poll2queryJob, eraseJob } from '@remote/api/adapter/helper/get-job'
import { getErrorMessage } from '@remote/api/adapter/helper/get-download-error-msg'
import { IRequestInterceptorParams } from '@remote/api/adapter/index.d'
import { logFactory } from '@remote/utils/logger'
import { DownloadError, TASK_ALREADY_EXISTS } from '@remote/api/adapter/helper/get-download-error-msg'
import { getDefaultPath } from '@remote/store/hezi-file/disk'
import { 
  LOCAL_STATUS_TASK_ALREADY_EXISTS, 
  LOCAL_STATUS_PARSE_FAIL, 
  LOCAL_STATUS_DOWNLOAD_TORRENT, 
  LOCAL_STATUS_PARSEING,
  LOCAL_STATUS_PARSE_FINISH,
} from '@remote/store/task'
import {
  IGetResourceListParams,
  IQueryLixianTasksRes,
} from './index.d'

import { querySubmitLixianTasksResult } from './helper'

export const events = {
  update: 'update',
  finish: 'finish',
}

const EVENTNAMES = [events.update, events.finish]
const TIMEOUT_DURATION = 4 * 60 * 1000

const log = logFactory('LixianTasks')

/*
批量任务离线离线下载类
*/
class LixianTasks extends EventEmitter {
  params: IGetResourceListParams
  groupId: string
  urls: string[]
  jobsId: string[]
  jobId2Job: { [jobId: string]: IQueryLixianTasksRes }
  url2Job: { [url: string]: IQueryLixianTasksRes }
  url2UpdateTimestampMap: { [url: string]: number }
  queryLixianTasksResolve: boolean

  constructor (params: IGetResourceListParams) {
    super(EVENTNAMES)
    this.params = params
    this.queryLixianTasksResolve = false
    this.jobId2Job = {}
    this.url2Job = {}
    this.url2UpdateTimestampMap = {}
    this.processSubmitUrlResut = this.processSubmitUrlResut.bind(this)
    this.updateTaskTimestamp = this.updateTaskTimestamp.bind(this)
  }
  // 启动解析入口
  async init () {
    const { userid, urls, downloadPath = getDefaultPath(), onlyVideo } = this.params
    const [magnets = [], downloadUrls = []] = matchBatchBtLink(urls, true)
    const willDownloadUlrs = [...downloadUrls, ...magnets]

    this.urls = willDownloadUlrs.slice()
    this.groupId = guid()

    const submitLixianTasksRes = await submitLixianTasks({
      group_id: this.groupId,
      user_id: userid,
      client_id: '',
      plat_form: 'pc',
      urls: willDownloadUlrs.map(url => ({ url, download_path: downloadPath })),
      only_video: onlyVideo,
    })

    if (submitLixianTasksRes.ret === 0) {
      // 查询离线submitLixianTasks的调用结果, 并生成jobId2Job、url2Job
      querySubmitLixianTasksResult(this.groupId, this.processSubmitUrlResut)
      // 查询上报作业结果，主要处理torrent下载
      this.queryJobResult()

      // 开始计时，统计任务超时
      willDownloadUlrs.forEach(this.updateTaskTimestamp)
    } else {
      // handle error all task
      willDownloadUlrs.forEach(url => {
        log('trigger init')
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, submitLixianTasksRes.msg || '任务提交失败-[001]')
      })
    }
  }
  // 处理提交原始链接返回结果
  processSubmitUrlResut (data: IQueryLixianTasksRes[]): boolean {
    const { urls } = this
    // 生成动态的jobId2Job、url2Job供 queryJobResult 处理
    const createJobTask = task => {
      if (!this.jobId2Job[task.job_id]) {
        if (task.errno === 0 || !task.errno) {
          // 处理用户在未查询到job，就进行了操作
          if (this.url2Job[task.url]) {
            task.is_pause = !!this.url2Job[task.url].is_pause
            log('trigger processSubmitUrlResut 1', task)
            this.trigger(events.update, task.url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
          } else {
            log('trigger processSubmitUrlResut 2', task)
            // 非磁力链接已创建下载任务、磁力链接进入下载torrent文件环节
            this.trigger(events.update, task.url, isMagnetTypeBTLink(task.url) ? LOCAL_STATUS_DOWNLOAD_TORRENT : LOCAL_STATUS_PARSEING)
          }
          // 创建动态的job
          this.jobId2Job[task.job_id] = task
          this.url2Job[task.url] = task

          this.updateTaskTimestamp(task.url)
        } else {
          // 处理错误
          task.is_pause = true
          task.is_process = true
          this.jobId2Job[task.job_id] = task
          this.url2Job[task.url] = task
          const localStatus = task.errno === TASK_ALREADY_EXISTS ? LOCAL_STATUS_TASK_ALREADY_EXISTS : LOCAL_STATUS_PARSE_FAIL
          const url = this.jobId2Job[task.job_id].url
          log('trigger processSubmitUrlResut 3', task)
          this.trigger(events.update, url, localStatus, DownloadError[task.errno] || `${task.errno}-torrent下载失败`)
          this.updateTaskTimestamp(url)
        }
      }
    }

    data.forEach(createJobTask)
    this.queryLixianTasksResolve = urls.length === data.length
    log('processSubmitUrlResut-', this.urls, data, this.queryLixianTasksResolve)
    return this.queryLixianTasksResolve
  }

  queryJobResult () {
    const { deviceId, userid } = this.params
    const biz = 'transport_download'
    const options = {
      method: 'POST',
      apiEnv: 'heziDevelopment',
      headers: { Target: deviceId },
    } as IRequestInterceptorParams['options']

    const params = {
      device_id: deviceId,
      userid,
      biz,
      jobIds: [],
      options: {
        onCheck: (jobs) => {
          if (jobs.length) this.processTorrents(jobs)
          console.log('processSubmitUrlResut', jobs)
          return ['', jobs.every(job => job.status === 1) && this.queryLixianTasksResolve]
        },
        timeout: 500 * 1000,
        interval: 3 * 1000,
        leading: true,
      },
    } as IPoll2queryJobparams
    this.poll2queryDynamicJob(params, options)
  }
  // 轮询作业，查询下载状态
  poll2queryDynamicJob (params: IPoll2queryJobparams, reqOptions: IRequestInterceptorParams['options'] = {}): Promise<IJobInfo[]> {
    const { device_id, userid, biz, options } = params
    const { timeout = 2 * 60 * 1000, interval = 5 * 1000, leading = false, onCheck } = options
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this
    return new Promise((resolve, reject) => {
      const startTime = Date.now()
      let timer: NodeJS.Timer | null = null

      function check () {
        const jobIds = Object.keys(that.jobId2Job)
        // 过滤torrent process中正在处理的任务
        const targetJobs = jobIds?.filter(id => !that.jobId2Job[id].is_process)
        if (!jobIds?.length || !targetJobs?.length) {
          const isResolved = that.queryLixianTasksResolve
          if (isResolved) {
            resolve([])
            log('poll2queryDynamicJob-1-退出轮询')
            timer && clearInterval(timer)
            log('trigger poll2queryDynamicJob check finish - 1')
            that.trigger(events.finish)
          }
          return isResolved
        }

        const getJobInfoParams = {
          device_id,
          user_id: userid,
          biz,
          job_ids: targetJobs,
          page_nums: targetJobs.length,
        }

        const handleJobInfo =(res) => {
          const [err, { data }] = res
          log('poll2queryDynamicJob - getJobInfo -', res)
          that.checkTaskTimestamp()
          if (!err && data?.list?.length) {
            const { list } = data
            const jobs = Array.isArray(list) ? list : []
            const targetJobs = jobs.filter(t => jobIds?.includes(t.job_id))

            if (targetJobs && targetJobs.length) {
              const [errorMsg, isResolved] = onCheck(targetJobs)
              if (errorMsg) {
                reject(errorMsg)
                eraseJob({ biz, device_id, job_id: jobIds }, reqOptions)
                if (timer) {
                  clearInterval(timer)
                  log('trigger poll2queryDynamicJob check finish - 2')
                  that.trigger(events.finish)
                  log('poll2queryDynamicJob-2-退出轮询')
                }
              }

              if (isResolved) {
                resolve(targetJobs)
                // eraseJob({ biz, device_id, job_id: jobIds }, reqOptions)
                const jobIds = Object.keys(that.jobId2Job)
                const willProcessJobs = jobIds?.filter(id => !that.jobId2Job[id].is_process)
                if (timer && !willProcessJobs.length) {
                  log('poll2queryDynamicJob-3-退出轮询')
                  clearInterval(timer)
                  log('trigger poll2queryDynamicJob check finish - 3')
                  that.trigger(events.finish)
                }
              }
            }
          }
        }

        getJobInfo(getJobInfoParams, reqOptions)
          .then(handleJobInfo)
      }

      timer = setInterval(() => {
        const now = Date.now()
        if (now - startTime > timeout) {
          reject('链接解析超时')
          if (timer) clearInterval(timer)
        }
        check()
      }, interval)

      if (leading)  check()
    })
  }

  // 再次提交torrentw文件
  async processTorrents (jobInfos: IJobInfo[]) {
    const torrent2JobMap = {}
    const torrents: string[] = []
    const willEraseJobs: string[] = []
    const makeJob2Torrent = (jobInfo: IJobInfo) => {
      const extData = safeJSONParse(jobInfo?.ext_data) as { name: string; dir: string }
      const { dir, name } = extData
      const torrentPath = `${dir}/${name}`
      const jobIdMapInfo = this.jobId2Job[jobInfo.job_id]
      // 处理http等链接——为避免有非磁力链接混入而做的特殊处理
      if (isOtherTypeBTLink(jobIdMapInfo.url)) {
        // 可优化本地任务为在线任务
        this.jobId2Job[jobInfo.job_id].is_process = true
        this.jobId2Job[jobInfo.job_id].torrent = jobIdMapInfo.url
        log('trigger processTorrents -1 isOtherTypeBTLink')
        this.trigger(events.update, jobIdMapInfo.url || jobIdMapInfo.download_id, LOCAL_STATUS_PARSE_FINISH)
        this.removeTaskTimestamp(jobIdMapInfo.url)
      } else {
        const url = this.jobId2Job[jobInfo.job_id].url
        if (jobInfo.status === 1 && torrentPath) {
          willEraseJobs.push(jobInfo.job_id)
          // 标记为正在处理中
          if (jobIdMapInfo) {
            this.jobId2Job[jobInfo.job_id].is_process = true
            this.jobId2Job[jobInfo.job_id].torrent = torrentPath
            // 用户中断处理只在此处拦截
            if (this.jobId2Job[jobInfo.job_id].is_pause) {
              log('trigger processTorrents -2 任务已取消')
              this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, '任务已取消')
              this.removeTaskTimestamp(jobIdMapInfo.url)
            } else {
              torrents.push(torrentPath)
              torrent2JobMap[torrentPath] = this.jobId2Job[jobInfo.job_id]
              log('trigger processTorrents -3 LOCAL_STATUS_PARSEING')
              this.trigger(events.update, url, LOCAL_STATUS_PARSEING)
              this.updateTaskTimestamp(jobIdMapInfo.url)
            }
          }
        } else if (jobInfo.status === 5) {
          willEraseJobs.push(jobInfo.job_id)
          this.jobId2Job[jobInfo.job_id].is_process = true
          log('trigger processTorrents -4 链接解析失败')
          this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, getErrorMessage(jobInfo?.ext_data) || '[job]链接解析失败')
          this.removeTaskTimestamp(jobIdMapInfo.url)
        }
      }
    }

    jobInfos.forEach(makeJob2Torrent)

    if (torrents.length) {
      this.submitTorrents(torrents, torrent2JobMap as { string: IQueryLixianTasksRes })
      const { deviceId } = this.params
      eraseJob({ biz: 'transport_download', device_id: deviceId, job_id: willEraseJobs }, {
        method: 'POST',
        apiEnv: 'heziDevelopment',
        headers: { Target: deviceId },
      })
    }
  }
  // 提交torrent文件
  async submitTorrents (torrents: string[], torrentMaps: { string: IQueryLixianTasksRes }) {
    const { downloadPath = getDefaultPath(), userid, onlyVideo } = this.params
    const groupId = guid()

    const submitLixianTasksRes = await submitLixianTasks({
      group_id: groupId,
      user_id: userid,
      client_id: '',
      plat_form: 'pc',
      urls: torrents.map(url => ({ url, download_path: downloadPath })),
      only_video: onlyVideo,
    })

    if (submitLixianTasksRes.ret === 0) {
      querySubmitLixianTasksResult(groupId, this.processSubmitTorrentResult.bind(this, torrentMaps))
    } else {
      // handle submit torrent error
      Object.values(torrentMaps).forEach((job: IQueryLixianTasksRes) => {
        log('trigger submitTorrents 提交失败[002]')
        this.trigger(events.update, job.url, LOCAL_STATUS_PARSE_FAIL, '提交失败[002]')
        this.removeTaskTimestamp(job.url)
      })
    }
    log('submitTorrents', submitLixianTasksRes)
  }
  // 处理提交torrent文件结果
  processSubmitTorrentResult (torrentMaps: { string: IQueryLixianTasksRes }, data: IQueryLixianTasksRes[]) {
    data.forEach(res => {
      const jobId = torrentMaps[res.url].job_id
      const url = this.jobId2Job[jobId]?.url || res.url
      if (res.errno === 0 || !res.errno) {
        log('trigger processSubmitTorrentResult - 1')
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FINISH)
        delete this.jobId2Job[jobId]
      } else if (res.errno === TASK_ALREADY_EXISTS) {
        log('trigger processSubmitTorrentResult - 2 解析失败')
        this.trigger(events.update, url, LOCAL_STATUS_TASK_ALREADY_EXISTS, DownloadError[res.errno] || `${res.errno}-解析失败`)
      } else {
        log('trigger processSubmitTorrentResult - 3 解析失败')
        // 处理失败
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, DownloadError[res.errno] || `${res.errno}-解析失败`)
      }
      this.removeTaskTimestamp(url)
    })
    this.checkTaskTimestamp()
    return Object.keys(torrentMaps).length === data.length
  }
  // 处理任务取消或暂停
  handlePause (url: string) {
    log('handlePause:', url, this.url2Job[url])
    if (this.url2Job[url]) {
      this.url2Job[url].is_pause = true
    } else {
      // @ts-ignore
      this.url2Job[url] = { is_pause: true, url }
      log('handlePause warn', url, '任务还未查询到job')
    }
    this.removeTaskTimestamp(url)
  }
  // 处理任务删除
  handleDelete (url: string) {
    log('handleDelete:', url, this.url2Job[url])
    if (this.url2Job[url]) {
      this.url2Job[url].is_pause = true
    } else {
      // @ts-ignore
      this.url2Job[url] = { is_pause: true, url }
      log('handleDelete warn', url, '任务还未查询到job')
    }
    this.removeTaskTimestamp(url)
  }
  poll2querySingleJob (jobId: string, isForceCheck = true) {
    const { userid, deviceId } = this.params
    const biz = 'transport_download'
    const params = {
      device_id: deviceId,
      userid,
      biz,
      jobIds: [jobId],
      options: {
        onCheck: (jobs) => {
          let errorMsg = ''
          const rejectJob = jobs.find(job => job.status === 5)
          if (rejectJob) errorMsg = getErrorMessage(rejectJob.ext_data)
          this.checkTaskTimestamp()
          return [errorMsg, isForceCheck ? jobs.every(job => job.status === 1) : true]
        },
        timeout: 500 * 1000,
        interval: 3 * 1000,
        leading: true,
      },
    }
    const options = {
      method: 'POST',
      apiEnv: 'heziDevelopment',
      headers: { Target: deviceId },
    }
    // @ts-ignore
    return poll2queryJob(params, options)
      .then(res => [null, res])
      .catch(err => [err, null])
  }
  async processSingleUrl (url: string) {
    // 重走下载torrent -> 提交任务流程
    const { userid, downloadPath = getDefaultPath(), onlyVideo } = this.params
    const groupId = guid()
    this.updateTaskTimestamp(url)
    const submitLixianTasksRes = await submitLixianTasks({
      group_id: groupId,
      user_id: userid,
      client_id: '',
      plat_form: 'pc',
      urls: [{ url, download_path: downloadPath }],
      only_video: onlyVideo,
    })
    log('processSingleUrl-1', submitLixianTasksRes)
    if (submitLixianTasksRes.ret === 0) {
      const [error, data] = await querySubmitLixianTasksResult(groupId)
      log('processSingleUrl-2', error)
      if (error) {
        this.removeTaskTimestamp(url)
        log('trigger processSingleUrl - 1', error)
        return this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, error)
      }
      const [jobError, jobs] = await this.poll2querySingleJob(data[0].job_id)
      if (jobError) {
        log('processSingleUrl-4', jobError)
        log('trigger processSingleUrl - 2', jobError)
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, jobError)
        this.removeTaskTimestamp(url)
      } else {
        this.processTorrents(jobs)
      }
    } else {
      // todo error
      log('processSingleUrl-3', submitLixianTasksRes.msg)
      log('trigger processSingleUrl - 3', submitLixianTasksRes.msg)
      this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, submitLixianTasksRes.msg || '[submit]提交链接解析失败')
      this.removeTaskTimestamp(url)
    }
  }
  // 处理任务重试
  async handleRestart (url: string) {
    log('handleRestart:', url, this.url2Job[url])
    if (this.url2Job[url]) {
      this.url2Job[url].is_pause = false
      const job = this.url2Job[url]
      // 如果有torrent文件
      if (job.torrent || isOtherTypeBTLink(url)) {
        this.submitTorrents([job.torrent || url], { [job.torrent || url]: job } as { string: IQueryLixianTasksRes })
      } else {
        this.processSingleUrl(url)
      }
    } else {
      // @ts-ignore
      this.url2Job[url] = { is_pause: false, url }
      log('handleRestart warn', url, '任务还未查询到job')
    }
    this.updateTaskTimestamp(url)
  }
  // 处理已存在任务强制重新下载
  async handleExistTaskReCreate (url: string) {
    log('handleExistTaskReCreate-1', this.url2Job[url])
    if (!this.url2Job[url]) throw '任务不存在'
    const { userid, downloadPath = getDefaultPath() } = this.params
    this.updateTaskTimestamp(url)
    const res = await createLixianTasks({
      user_id: userid,
      url: this.url2Job[url].torrent as string || url,
      plat_form: 'pc',
      client_id: '',
      download_path: downloadPath,
      recreate: true,
    })
    log('handleExistTaskReCreate-2', res)
    if (res.ret === 0) {
      const [jobError, jobs] = await this.poll2querySingleJob(res.result.job_id, false)
      log('createLixianTasks-3', jobs)
      if (jobError) {
        log('trigger handleExistTaskReCreate - 1', jobError)
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, jobError)
      } else {
        log('trigger handleExistTaskReCreate - 2')
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FINISH)
      }
    } else {
      log('trigger handleExistTaskReCreate - 3')
      // todo error
      this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, res.msg || '[CLX]链接解析失败')
    }
    this.removeTaskTimestamp(url)
  }

  updateTaskTimestamp (url: string) {
    this.url2UpdateTimestampMap[url] = Date.now()
  }

  removeTaskTimestamp (url: string) {
    delete this.url2UpdateTimestampMap[url]
  }

  checkTaskTimestamp () {
    const keys = Object.keys(this.url2UpdateTimestampMap)
    const now = Date.now()
    keys.forEach(url => {
      const lastUpdate = this.url2UpdateTimestampMap[url]
      const diff = now - lastUpdate
      log('checkTaskTimestamp', diff)
      if (lastUpdate && diff > TIMEOUT_DURATION) {
        delete this.url2UpdateTimestampMap[url]
        this.url2Job[url].is_pause = true
        log('trigger checkTaskTimestamp', url)
        this.trigger(events.update, url, LOCAL_STATUS_PARSE_FAIL, '链接解析超时[003]', true)
      }
    })
  }
}

export default LixianTasks